import classnames from 'classnames';
import React, { useMemo } from 'react';
import Select from 'react-select';
import { isNull } from 'lodash/fp';

import { capitaliseStartLetter } from '../../../utils/regex-utils';
import { customStyles, DropdownOptions } from '../../shared/dropdown/Dropdown';
import { CustomTooltip } from '../../shared/tooltip';
import styles from './Fields.module.scss';
import { getOriginalDocument, getSecondaryDocument } from '../../documents/my-documents/store';
import { ArkDocument } from '../../documents/store/types';
import { useAppSelector } from '../../../hooks/react-redux';
import { DATE_SLASH_SEPARATED_FORMAT, formatDate } from '../../../utils/luxon';

interface DocumentFieldProps {
    id: string;
    documentProperty: keyof ArkDocument | null;
    isTable?: boolean;
    showClause?: boolean;
    includedInAnnex?: boolean;
    modalInstance?: boolean;
}

export const DocumentField: React.FC<DocumentFieldProps> = ({ id, showClause = false, isTable = false, documentProperty }) => {
    const originalDocument = useAppSelector(getOriginalDocument);
    const secondaryDocument = useAppSelector(getSecondaryDocument);

    const documentData = useMemo(() => secondaryDocument || originalDocument, [secondaryDocument, originalDocument]);

    const documentValue = useMemo(() => (documentData && documentProperty && documentData[documentProperty] as string | null) || null, [documentData, documentProperty]);

    const value = useMemo(() => {
        if (isNull(documentValue) || isNull(documentProperty)) {
            return null;
        }
        if (['entityAName', 'entityBName', 'documentName'].includes(documentProperty)) {
            return [documentValue];
        }
        if (['executedDate', 'datedAsOf'].includes(documentProperty)) {
            return isNull(documentValue) ? null : [formatDate(documentValue, DATE_SLASH_SEPARATED_FORMAT)];
        }
        return null;
    }, [documentValue, documentProperty]);

    const mappedValue: DropdownOptions = value?.map(value => ({ value, label: capitaliseStartLetter(value) }));

    const isMulti = useMemo(() => !!value && value.length > 1, [value]);

    const dropdownTooltip = useMemo(() => {
        if (!value) {
            return null;
        }
        return value[0];
    }, [value]);

    return (
        <CustomTooltip overlayText={dropdownTooltip} trigger='click'>
            <div
                data-testid={`definition-document-${id}`}
                className={classnames(styles.dropdownFieldWrapper, { [styles.withClauseLabel]: showClause && isTable })}
            >
                <Select
                    className={styles.dropdownField}
                    classNamePrefix='ark-dropdown'
                    isDisabled
                    value={mappedValue}
                    isMulti={isMulti}
                    styles={customStyles}
                    isClearable={false}
                    hideIndicator
                    minControlHeight='38px'
                    fontWeight={500}
                />
            </div>
        </CustomTooltip>
    );
};
